// REG-802: the read-only stat line at the bottom of a column's three-dot menu.
// Grids ask PostgREST for the aggregate over the whole filtered set
// (pgrst.db_aggregates_enabled), not over the loaded page. This module is pure: it plans the aggregate, names the select, and formats
// the result, so every grid shares one set of rules.

export type ColumnStatFormat = 'money' | 'percent' | 'number' | 'integer';

export type ColumnStatPlan =
  /** Money and plain number columns: Total and Average. */
  | { kind: 'total'; column: string; format: ColumnStatFormat }
  /** Percent, factor and term columns: a total means nothing. */
  | { kind: 'average'; column: string; format: ColumnStatFormat; digits: number }
  /** Text, picklist, lookup and yes/no columns. */
  | { kind: 'mode'; column: string; labelColumn?: string; boolean?: boolean }
  /** Dates and id-like numbers: lowest and highest. */
  | { kind: 'range'; column: string; date: boolean };

export interface ColumnStatField {
  column: string;
  dataType: string;
  isCurrency?: boolean;
  isPercentage?: boolean;
  /** Decimal places the grid shows for this column, when it says. */
  precision?: number | null;
  /** Relationship and report label columns hold the readable value. */
  labelColumn?: string;
  /** Picklist or relationship: group by value even when the type is numeric. */
  categorical?: boolean;
}

/** Groups fetched for a most-common value; more than this is free text. */
export const MODE_GROUP_CAP = 1000;

const NUMERIC_TYPES = ['int', 'numeric', 'float', 'real', 'double', 'decimal', 'money'];

function isNumericType(dataType: string): boolean {
  const type = dataType.toLowerCase();
  return NUMERIC_TYPES.some((name) => type.includes(name));
}

function isDateType(dataType: string): boolean {
  const type = dataType.toLowerCase();
  return type.includes('date') || type.includes('timestamp');
}

function isIdLike(column: string): boolean {
  return column === 'id' || column.endsWith('_id') || column.endsWith('_number');
}

/** Average-only numbers: the total of a factor, term or rate is meaningless. */
function isAverageOnly(column: string): boolean {
  return /(^|_)(factor|term|rate|ratio|percentage|pct|score)(_|$)/.test(column)
    || column.startsWith('term');
}

export function columnStatPlan(field: ColumnStatField): ColumnStatPlan | null {
  const { column, dataType } = field;
  if (!column || column.startsWith('__')) return null;
  const type = (dataType ?? '').toLowerCase();
  if (type.includes('json') || type.endsWith('[]') || type === 'tsvector') return null;

  if (field.categorical || field.labelColumn) {
    return { kind: 'mode', column, ...(field.labelColumn ? { labelColumn: field.labelColumn } : {}) };
  }
  if (type === 'boolean') return { kind: 'mode', column, boolean: true };
  if (isDateType(type)) return { kind: 'range', column, date: true };
  if (isNumericType(type)) {
    if (isIdLike(column) && !field.isCurrency) return { kind: 'range', column, date: false };
    const integer = type.includes('int');
    if (field.isPercentage) {
      // An average of whole percents still needs a decimal (12.4%, not 12%).
      return { kind: 'average', column, format: 'percent', digits: Math.max(field.precision ?? 1, 1) };
    }
    if (field.isCurrency) return { kind: 'total', column, format: 'money' };
    if (isAverageOnly(column)) {
      return { kind: 'average', column, format: 'number', digits: field.precision ?? 2 };
    }
    return { kind: 'total', column, format: integer ? 'integer' : 'number' };
  }
  return { kind: 'mode', column };
}

/**
 * The PostgREST select for a plan. Counts use `id` when the source has one:
 * a bare count() becomes COUNT(view.*), which builds every calculated column.
 */
export function columnStatSelect(plan: ColumnStatPlan, countColumn: string | null): string {
  const count = countColumn ? `${countColumn}.count()` : 'count()';
  switch (plan.kind) {
    case 'total':
      return `${plan.column}.sum(),${plan.column}.avg()`;
    case 'average':
      return `${plan.column}.avg()`;
    case 'range':
      return `${plan.column}.min(),${plan.column}.max()`;
    case 'mode': {
      // A label column given as a JSON path needs an alias to come back named.
      const label = plan.labelColumn
        ? `,lbl:${plan.labelColumn}`
        : '';
      return `${plan.column}${label},${count}`;
    }
  }
}

export interface ColumnStatResult {
  /** One or two short read-outs, e.g. ["Total $74,365,024", "Average $44,321"]. */
  lines: string[];
}

const moneyFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const integerFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function formatNumber(value: number, format: ColumnStatFormat, digits = 2): string {
  switch (format) {
    case 'money': return moneyFormat.format(value);
    case 'percent': return `${value.toFixed(digits)}%`;
    case 'integer': return integerFormat.format(value);
    case 'number':
    default:
      return new Intl.NumberFormat('en-US', { maximumFractionDigits: digits }).format(value);
  }
}

function formatDate(value: unknown): string {
  if (!value) return '';
  const text = String(value);
  // Plain dates are calendar days; parsing them as UTC would show the day before.
  const date = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T00:00:00`) : new Date(text);
  return Number.isNaN(date.getTime())
    ? text
    : new Intl.DateTimeFormat('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }).format(date);
}

function modeLabel(value: unknown, plan: Extract<ColumnStatPlan, { kind: 'mode' }>): string {
  if (plan.boolean) return value === true || value === 'true' ? 'Yes' : 'No';
  return String(value);
}

/** Formats the aggregate rows PostgREST returns for columnStatSelect(plan). */
export function formatColumnStat(plan: ColumnStatPlan, rows: Record<string, unknown>[]): ColumnStatResult {
  const empty = { lines: ['No values'] };
  switch (plan.kind) {
    case 'total': {
      const sum = toNumber(rows[0]?.sum);
      const avg = toNumber(rows[0]?.avg);
      if (sum === null) return empty;
      const lines = [`Total ${formatNumber(sum, plan.format, 2)}`];
      if (avg !== null) lines.push(`Average ${formatNumber(avg, plan.format === 'integer' ? 'number' : plan.format, 2)}`);
      return { lines };
    }
    case 'average': {
      const avg = toNumber(rows[0]?.avg);
      return avg === null ? empty : { lines: [`Average ${formatNumber(avg, plan.format, plan.digits)}`] };
    }
    case 'range': {
      const min = rows[0]?.min;
      const max = rows[0]?.max;
      if (min == null || max == null) return empty;
      const show = plan.date ? formatDate : (value: unknown) => integerFormat.format(Number(value));
      const low = show(min);
      const high = show(max);
      return low === high
        ? { lines: [plan.date ? `Only ${low}` : `All ${low}`] }
        : { lines: [plan.date ? `Earliest ${low}` : `Lowest ${low}`, plan.date ? `Latest ${high}` : `Highest ${high}`] };
    }
    case 'mode': {
      if (rows.length >= MODE_GROUP_CAP) {
        return { lines: [`${integerFormat.format(MODE_GROUP_CAP)}+ different values`] };
      }
      let best: { label: string; count: number } | null = null;
      let distinct = 0;
      rows.forEach((row) => {
        const value = plan.labelColumn ? (row.lbl ?? row[plan.column]) : row[plan.column];
        if (value === null || value === undefined || value === '') return;
        distinct += 1;
        const count = toNumber(row.count) ?? 0;
        if (!best || count > best.count) best = { label: modeLabel(value, plan), count };
      });
      if (!best) return empty;
      const top = best as { label: string; count: number };
      // Every value once: a "most common" pick would just be the first row.
      if (top.count <= 1 && distinct > 1) {
        return { lines: [`${integerFormat.format(distinct)} different values`] };
      }
      return { lines: [`Most common: ${top.label} (${integerFormat.format(top.count)})`] };
    }
  }
}

/**
 * The same aggregate over values already in the browser, in the row shape
 * PostgREST returns, so formatColumnStat handles both. Used where a grid's
 * column filters run client-side and cannot be sent to the server.
 */
export function aggregateValuesForPlan(plan: ColumnStatPlan, values: unknown[]): Record<string, unknown>[] {
  const present = values.filter((value) => value !== null && value !== undefined && value !== '');
  switch (plan.kind) {
    case 'total':
    case 'average': {
      const numbers = present.map(toNumber).filter((value): value is number => value !== null);
      if (numbers.length === 0) return [{ sum: null, avg: null }];
      const sum = numbers.reduce((total, value) => total + value, 0);
      return [{ sum, avg: sum / numbers.length }];
    }
    case 'range': {
      // Grid display values can decorate numbers ("#97153").
      const usable = plan.date
        ? present.filter((value) => Number.isFinite(new Date(String(value)).getTime()))
        : present.map((value) => toNumber(String(value).replace(/[^0-9.-]/g, ''))).filter((value) => value !== null);
      if (usable.length === 0) return [{ min: null, max: null }];
      const key = (value: unknown) => (plan.date ? new Date(String(value)).getTime() : Number(value));
      const sorted = [...usable].sort((a, b) => key(a) - key(b));
      return [{ min: sorted[0], max: sorted[sorted.length - 1] }];
    }
    case 'mode': {
      const counts = new Map<string, { value: unknown; count: number }>();
      present.forEach((value) => {
        const key = String(value);
        const entry = counts.get(key);
        if (entry) entry.count += 1;
        else counts.set(key, { value, count: 1 });
      });
      return [...counts.values()].map(({ value, count }) => ({ [plan.column]: value, count }));
    }
  }
}

/** A statement timeout reads better as advice than as a database error. */
export function columnStatErrorText(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  const message = String((error as { message?: string } | null)?.message ?? '');
  if (code === '57014' || /statement timeout/i.test(message)) return 'Too many rows to total, add a filter';
  return 'Could not load the total';
}
